/* Greenleaf — the collection filter bar's mobile behaviour.
 *
 * WHY THIS FILE EXISTS. The filters are <details>/<summary> and were built with
 * "nothing here needs a script" as a deliberate property — the browser supplies
 * keyboard operation, screen-reader semantics and a working panel with scripting
 * off, and each option is a real link. That is all still true and none of it is
 * replaced here.
 *
 * What a script IS needed for is the phone. Below 600px the panel is a bottom
 * sheet pinned to the edge of the screen (see .filt__panel in custom.css), and a
 * sheet needs three things a <details> cannot give it: something to dim behind
 * it, a way out that is not the chip it may have scrolled past, and the page
 * behind it held still. Everything this file adds is an enhancement on top of a
 * panel that already works without it; with the script blocked the sheet still
 * opens, still filters, and still closes from its own chip.
 *
 * The scrim, the Done button and the scroll lock are all gated on the same
 * 600px query the CSS uses, read through matchMedia so a rotation or a resize
 * re-evaluates rather than leaving a scrim on a wide screen.
 *
 * DESKTOP, 26 Sep 2026. Andrew's screenshot of /collections/colored-stones: the
 * dropdowns stacked in a pile. Every <details> could be open at once, each
 * later panel painted over the one before it, so their labels read as cut off
 * ("Aquama", "Cockt") and the counts sat under the next panel; and a panel
 * with an active filter is rendered `open` by the Liquid, so arriving with two
 * filters applied put two panels over the grid before anyone clicked. Now, at
 * every width: one panel open at a time, Esc closes it, and on desktop a click
 * outside the bar closes it too. The panels the Liquid opened are closed on
 * arrival, at every width (the word still shows the count, and Clear all is there);
 * a panel that would run past the right edge of the window opens leftward
 * instead (`.filt__panel--end`). Without the script, all of this degrades to
 * plain <details> that still work.
 */
(function () {
  'use strict';

  var bar = document.querySelector('.filterbar');
  if (!bar) return;

  var sheet = window.matchMedia('(max-width:600px)');
  var scrim = null;

  function panels() {
    return bar.querySelectorAll('details.filt');
  }

  function isSheet() {
    return sheet.matches;
  }

  /* The scroll lock is `overflow:hidden` on <html>, matching the drawer's choice
   * in gl-header.js for the same reason recorded there: `position:fixed` on the
   * body loses the scroll position, so closing the sheet would jump the customer
   * back to the top of a collection they had scrolled halfway down. */
  function lock(on) {
    if (on) document.documentElement.style.overflow = 'hidden';
    else document.documentElement.style.removeProperty('overflow');
  }

  function removeScrim() {
    if (scrim && scrim.parentNode) scrim.parentNode.removeChild(scrim);
    scrim = null;
  }

  function closeAll(focusBack) {
    var list = panels();
    for (var i = 0; i < list.length; i++) {
      if (list[i].open) {
        list[i].open = false;
        if (focusBack) {
          var sum = list[i].querySelector('summary');
          if (sum) { try { sum.focus({ preventScroll: true }); } catch (e) { sum.focus(); } }
        }
      }
    }
  }

  function sync() {
    var open = null, list = panels(), i;
    for (i = 0; i < list.length; i++) if (list[i].open) open = list[i];

    if (!open || !isSheet()) {
      removeScrim();
      lock(false);
      return;
    }

    if (!scrim) {
      /* A <button>, not a <div>: it is a real control that closes the sheet, so
       * it should be reachable and announced as one rather than being a click
       * target only a mouse can find. */
      scrim = document.createElement('button');
      scrim.type = 'button';
      scrim.className = 'filt__scrim';
      scrim.setAttribute('aria-label', 'Close filters');
      scrim.addEventListener('click', function () { closeAll(true); sync(); });
      bar.appendChild(scrim);
    }
    scrim.style.display = 'block';
    lock(true);
  }

  /* A dropdown that would run past the right edge of the window opens leftward,
   * anchored to its word's right side. Measured each time it opens, because the
   * row wraps differently at every width. Not on a phone, where the panel is a
   * full-width sheet and has no edge to clear. */
  function place(d) {
    var panel = d.querySelector('.filt__panel');
    if (!panel) return;
    panel.classList.remove('filt__panel--end');
    if (isSheet()) return;
    var r = panel.getBoundingClientRect();
    var room = document.documentElement.clientWidth - 16;
    if (r.right > room) panel.classList.add('filt__panel--end');
  }

  /* One panel at a time, at every width. As bottom sheets two open panels stack
   * on the same edge of the screen and the one underneath is unreachable; as
   * dropdowns they overlap and each hides the labels of the one before it,
   * which is the pile Andrew sent a screenshot of on 26 Sep 2026. */
  function onToggle(e) {
    var d = e.target;
    if (!d || d.tagName !== 'DETAILS') return;
    if (d.open) {
      var list = panels();
      for (var i = 0; i < list.length; i++) if (list[i] !== d) list[i].open = false;
      place(d);
    }
    sync();
  }

  /* A click anywhere outside the open panel and its word closes it. Desktop
   * only: on a phone the scrim covers everything outside the sheet and already
   * does this. */
  function onOutside(e) {
    if (isSheet()) return;
    var list = panels();
    for (var i = 0; i < list.length; i++) {
      if (list[i].open && !list[i].contains(e.target)) list[i].open = false;
    }
  }

  function addDone() {
    var list = panels(), i, panel, btn;
    for (i = 0; i < list.length; i++) {
      panel = list[i].querySelector('.filt__panel');
      if (!panel || panel.querySelector('.filt__done')) continue;
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'filt__done';
      btn.textContent = 'Done';
      /* Closes its own sheet rather than all of them, so it reads as the panel's
       * own control. `closest` because the button is appended inside the panel. */
      btn.addEventListener('click', function (ev) {
        var d = ev.target.closest('details.filt');
        if (d) {
          d.open = false;
          var sum = d.querySelector('summary');
          if (sum) { try { sum.focus({ preventScroll: true }); } catch (e2) { sum.focus(); } }
        }
        sync();
      });
      panel.appendChild(btn);
    }
  }

  function init() {
    addDone();
    /* `toggle` does not bubble in every engine, so it is captured rather than
     * delegated — the same shape gl-media.js uses for its own <details>. */
    bar.addEventListener('toggle', onToggle, true);
    document.addEventListener('click', onOutside);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeAll(true); sync(); }
    });
    /* The Liquid renders a panel `open` when one of its filters is active, so
     * several can arrive open. All of them close on arrival, at every width:
     * on desktop they sat over the grid the customer had just filtered, and on
     * a phone the page came back behind a bottom sheet with scrolling locked,
     * so the result of picking a filter was hidden by the filter (29 Sep 2026,
     * bug hunt; the phone half was kept on 26 Sep and should not have been).
     * The word still carries its count and Clear all is still there. */
    var list = panels();
    for (var k = 0; k < list.length; k++) if (list[k].open) list[k].open = false;
    if (sheet.addEventListener) sheet.addEventListener('change', sync);
    else if (sheet.addListener) sheet.addListener(sync);
    sync();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
